/**
 * Editor (software publisher) legal info — used on invoices, certifications, legal pages.
 *
 * Source of truth: docs/certifications/editor.json — kept in sync manually.
 * Transition note: VisionPOS SAS will eventually replace LinkVision SAS. When that happens,
 * update both this file AND docs/certifications/editor.json.
 */

import { APP_BUILD_VERSION } from '@/lib/force-reload';

export const EDITOR = {
  name: 'LinkVision',
  legalName: 'LinkVision SAS',
  legalForm: 'SAS',
  capital: '10 000 €',
  siret: '93170077700010',
  siren: '931700777',
  tvaIntra: 'FR60931700777',
  addressLine1: '64 rue Waldeck Rousseau',
  postalCode: '69006',
  city: 'Lyon',
  country: 'France',
  email: 'hello@linkvision.fr',
  phone: '',
  softwareName: 'VisionPos 360',
  /**
   * Mention obligatoire NF525 — auto-certification éditeur au titre de
   * l'article 286, I-3° bis du CGI (loi de finances 2018). VisionPos n'a
   * PAS de certification AFNOR/INFOCERT tiers (qui nécessiterait audit
   * + paiement annuel). Toute mention "certifié NF525" sans qualificatif
   * "auto-" = usurpation pénalement répréhensible.
   */
  nf525Mention: 'Logiciel de caisse auto-certifié article 286 I-3° bis CGI',
  signatory: { name: 'Yassim Slimani', title: 'Président' },
  /**
   * Certificat de conformité éditeur (auto-certification, loi de finances pour 2026) émis au lancement :
   * version certifiée et date d'émission, reprises sur le certificat délivré à chaque commerçant.
   */
  certificate: { version: '1.5.9.2', issuedAt: '2026-04-18' },
} as const;

/** Identifiant logiciel imprimé sur les tickets : nom + version du build (loi anti-fraude). */
export const softwareIdentity = (version: string = APP_BUILD_VERSION): string =>
  `${EDITOR.softwareName} v${version}`;

/**
 * Mention d'auto-certification d'un ticket : celle du commerce (ticket_config.mention_nf525) ou, à
 * défaut, la mention éditeur — toujours complétée par le nom et la version du logiciel.
 */
export const ticketFiscalMention = (
  mention?: string | null,
  version: string = APP_BUILD_VERSION,
): string => {
  const base = mention?.trim() || EDITOR.nf525Mention;
  const identity = softwareIdentity(version);
  return base.includes(identity) ? base : `${base} — ${identity}`;
};

/** Bloc de pied de ticket : 3 lignes mention auto-cert + nom logiciel + éditeur */
export const editorTicketFooter = () => ({
  nf525: EDITOR.nf525Mention,
  software: `${softwareIdentity()} — www.visionpos.fr`,
  editor: `Éditeur : ${EDITOR.legalName}`,
});

export const editorFooterLine = () =>
  `${EDITOR.softwareName} — ${EDITOR.legalName} au capital de ${EDITOR.capital} — SIRET ${EDITOR.siret}`;
